import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Plus, X } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { updateAuth } from '../../store/slices/authSlice';
import { useTranslation } from 'react-i18next';
import ImageCropper from '../../components/common/ImageCropper';
import Spinner from '../../components/common/Spinner';
import { userService } from '../../services/userService';
import { mediaService } from '../../services/mediaService';

const RecentPics = ({ nextPath = '/auth/location', backPath = '/auth/looking-for', unauthenticatedPath = '/signup' }) => {
    const [images, setImages] = useState([null, null, null, null, null, null]);
    const [imageFiles, setImageFiles] = useState([null, null, null, null, null, null]); // Pour stocker les fichiers
    const [isUploading, setIsUploading] = useState(false);
    const [cropData, setCropData] = useState(null);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const authState = useSelector(state => state.auth);
    const { t } = useTranslation();

    const handleImageUpload = async (event, index) => {
        const file = event.target.files[0];
        if (!file) return;

        const imageUrl = URL.createObjectURL(file);
        setCropData({
            imageUrl,
            index,
            fileName: file.name
        });
    };

    const handleCropComplete = async (croppedFile) => {
        const index = cropData.index;
        try {
            // Créer une URL temporaire pour l'aperçu
            const imageUrl = URL.createObjectURL(croppedFile);
            
            // Mettre à jour les états
            const newImages = [...images];
            const newImageFiles = [...imageFiles];
            
            newImages[index] = imageUrl;
            newImageFiles[index] = croppedFile;
            
            setImages(newImages);
            setImageFiles(newImageFiles);
            setCropData(null);
        } catch (error) {
            console.error('Error handling cropped image:', error);
        }
    };

    const handleCropCancel = () => {
        setCropData(null);
    };

    const handleRemoveImage = (index) => {
        const newImages = [...images];
        const newImageFiles = [...imageFiles];
        newImages[index] = null;
        newImageFiles[index] = null;
        setImages(newImages);
        setImageFiles(newImageFiles);
    };

    const handleNext = async () => {
        const validImages = images.filter(img => img !== null);
        if (validImages.length < 2) {
            return;
        }

        setIsUploading(true);
        try {
            // Upload de toutes les images vers Cloudflare R2
            const uploadPromises = imageFiles
                .filter(file => file !== null)
                .map(file => mediaService.uploadImage(file));

            const uploadedImages = await Promise.all(uploadPromises);

            // Préparer les données des photos
            const photos = uploadedImages.map(({ photoUrl, objectKey }) => ({ photoUrl, objectKey }));
            const userData = {
                ...authState,
                photos,
                status: 'Active',
                profileCompleted: true
            };

            delete userData._id;
            delete userData.loading;
            delete userData.error;
            delete userData.location;

            const response = await userService.updateProfile(authState.uid, userData);

            if (response.status === 200) {
                dispatch(updateAuth(userData));
                navigate(nextPath);
            }
        } catch (error) {
            console.error('Error updating profile:', error);
        } finally {
            setIsUploading(false);
        }
    };

    if (!authState.uid) {
        navigate(unauthenticatedPath);
        return null;
    }

    return (
        <div className="page-wrapper">
            {isUploading && <Spinner />}
            {cropData && (
                <ImageCropper
                    image={cropData.imageUrl}
                    fileName={cropData.fileName}
                    onCropComplete={handleCropComplete}
                    onCancel={handleCropCancel}
                />
            )}
            <div className="content-body">
                <div className="container">
                    <div className="account-area style-2">
                        {/* Retour vers l'étape précédente de la logique du parcours (pas l'historique du navigateur : */}
                        {/* la redirection automatique de ProtectedAuthRoute peut remplacer l'historique réel) */}
                        <a href="#" onClick={(e) => { e.preventDefault(); navigate(backPath); }} className="back-btn">
                            <ChevronLeft className="icon" />
                        </a>
                        <div className="section-head ps-0">
                            <h2>{t('recentPics.title')}</h2>
                            <p>{t('recentPics.subtitle')}</p>
                        </div>

                        <div className="row g-3" data-masonry='{"percentPosition": true }'>
                            {images.map((image, index) => (
                                <div key={index} className="col-4">
                                    <div className="dz-drop-box style-2">
                                        {image ? (
                                            <img 
                                                src={image} 
                                                alt={t('recentPics.imageAlt', { number: index + 1 })} 
                                            />
                                        ) : (
                                            <img 
                                                src="/assets/images/recent-pic/drop-bx2.png" 
                                                alt={t('recentPics.defaultImageAlt')} 
                                            />
                                        )}
                                        <div className="drop-bx">
                                            <div 
                                                className={`imagePreview ${image ? 'active' : ''}`} 
                                                style={image ? { backgroundImage: `url(${image})` } : {}}
                                            >
                                                <label 
                                                    htmlFor={`imageUpload${index}`} 
                                                    className="add-btn"
                                                    aria-label={t('recentPics.addPhotoAriaLabel')}
                                                >
                                                    <Plus className="icon" />
                                                </label>
                                                {image && (
                                                    <div 
                                                        className="remove-img remove-btn"
                                                        onClick={() => handleRemoveImage(index)}
                                                        aria-label={t('recentPics.removePhotoAriaLabel')}
                                                    >
                                                        <X className="icon" />
                                                    </div>
                                                )}
                                                <input
                                                    type='file'
                                                    className="form-control d-none imageUpload"
                                                    id={`imageUpload${index}`}
                                                    accept=".png, .jpg, .jpeg"
                                                    onChange={(e) => handleImageUpload(e,index)}
                                                    aria-label={t('recentPics.uploadInputAriaLabel', { number: index + 1 })}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="text-center">
                            <button
                                className="btn btn-primary btn-lg rounded-pill px-5 mt-4"
                                onClick={handleNext}
                                disabled={isUploading || images.filter(img => img !== null).length < 2}
                            >
                                {t('recentPics.continue')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RecentPics;
